import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Client } from "resourceModels";
import { saveClientAction } from "actions";
import { updateOne } from "actions/resources";
import RequestState from "services/requestState";
import { getResource, getResourcesRequest } from "selectors/resources";
import Button from "components/Common/Button";
import SvgIcon from "components/Common/SvgIcon";
import colors from "stylesheets/utilities/colors.scss";
import isEmail from "validator/lib/isEmail";
import isURL from "validator/lib/isURL";
import { defaultHandoffSettings } from "../services";

import "./style.scss";

class ModalZendeskTicketingConfig extends Component {
  /**
   * @param {String} subdomain
   */
  onSubdomainChange(subdomain) {
    this.updateHandoffZendeskIntegration({ subdomain });
  }

  /**
   * @param {String} email
   */
  onEmailChange(email) {
    this.updateHandoffZendeskIntegration({ email });
  }

  /**
   * @param {String} token
   */
  onTokenChange(token) {
    this.updateHandoffZendeskIntegration({ token });
  }

  /**
   * @returns {Immutable.Map}
   */
  get zendeskConfig() {
    const {
      /** @type Client */
      client
    } = this.props;

    return defaultHandoffSettings.mergeDeep(client.handoff).getIn(["integrations", "zendesk"]);
  }

  /**
   * @returns {Boolean}
   */
  get isZendeskConfigured() {
    return Boolean(this.zendeskConfig.get("token"));
  }

  /**
   */
  handleSubmit() {
    const {
      /** @type Client */
      client,
      saveClient
    } = this.props;

    this.updateHandoffZendeskIntegration({ enabled: true });

    saveClient(client.id);
  }

  /**
   * Methods
   */

  /**
   * @param {Object} properties
   */
  updateHandoffZendeskIntegration(properties) {
    const {
      /** @type Client */
      client,
      updateClient
    } = this.props;

    updateClient(client.id, {
      handoff: client.handoff.mergeIn(
        ["integrations", "zendesk"],
        this.zendeskConfig.merge(properties)
      )
    });
  }

  /**
   * @returns {ReactElement}
   */
  render() {
    const {
      /** @type Client */
      client,
      /** @type RequestState */
      clientPatchRequest
    } = this.props;

    const zendesk = this.zendeskConfig;
    const isValidEmail = isEmail(zendesk.get("email"));
    const isValidToken = /\w{2,}/.test(zendesk.get("token"));
    const isValidSubdomain = !isURL(zendesk.get("subdomain")) && /\w+/.test(zendesk.get("subdomain"));

    return (
      <div className="ModalZendeskTicketingConfig Modal__modal">
        <h5 className="Modal__title">Configure Zendesk Ticketing</h5>
        <div className="Modal__content">
          <form className="ModalZendeskTicketingConfig__section g-form__block" autoComplete>
            <div className="ModalZendeskTicketingConfig}__section__input-container">
              <div className="ModalZendeskTicketingConfig__section__input-container__row">
                <div className="ModalZendeskTicketingConfig__section__input-container__input">
                  <label
                    className="g-input__label"
                    htmlFor="ModalZendeskTicketingConfig__contact-name"
                  >
                    Zendesk Subdomain
                    <SvgIcon
                      link="https://support.zendesk.com/hc/en-us/articles/221682747-Where-can-I-find-my-Zendesk-subdomain-"
                      customClassName="g-input__label"
                      icon="Help"
                      fillColor={colors.colorGrey3}
                      cursor="pointer"
                      height={20}
                    />
                  </label>
                  <input
                    ref={(e) => {
                      this.subdomain = e;
                    }}
                    autoComplete="off"
                    type="text"
                    className={`g-input ${isValidSubdomain || "g-input--invalid"}`}
                    placeholder="mycompany"
                    value={zendesk ? (zendesk.get("subdomain") || "") : ""}
                    onChange={e => this.onSubdomainChange(e.target.value)}
                    id="ModalZendeskTicketingConfig__contact-name"
                  />
                  <p className="g-form__block__description">
                    Your subdomain is the first part of your helpdesk URL: https://<b><u className="ModalZendeskTicketingConfig__section__input-container__input__subdomain">this</u></b>.zendesk.com
                  </p>
                </div>
                <div className="ModalZendeskTicketingConfig__section__input-container__input">
                  <label className="g-input__label" htmlFor="ModalZendeskTicketingConfig__email">
                    Zendesk Login Email
                  </label>
                  <input
                    ref={(e) => {
                      this.email = e;
                    }}
                    autoComplete="off"
                    type="text"
                    className={`g-input ${isValidEmail || "g-input--invalid"}`}
                    placeholder="you@yourcompany.com"
                    value={zendesk ? (zendesk.get("email") || "") : ""}
                    onChange={e => this.onEmailChange(e.target.value)}
                    id="ModalZendeskTicketingConfig__email"
                  />
                </div>
              </div>
              <div className="ModalZendeskTicketingConfig__section__input-container__input">
                <label className="g-input__label" htmlFor="ModalZendeskTicketingConfig__token">
                  Zendesk API Token
                </label>
                <input
                  ref={(e) => {
                    this.token = e;
                  }}
                  autoComplete="off"
                  type="text"
                  className={`g-input ${isValidToken || "g-input--invalid"}`}
                  placeholder="ABcDeFGhijKlMnOPqrSTuVwxyZ"
                  value={zendesk ? (zendesk.get("token") || "") : ""}
                  onChange={(e) => {
                    this.onTokenChange(e.target.value);
                  }}
                  id="ModalZendeskTicketingConfig__token"
                />
              </div>
              <p className="g-form__block__description">
                You can get one of these from Zendesk in Settings &gt; Channels &gt; API &gt;
                Token Access
              </p>
            </div>
          </form>
        </div>
        <div className="Modal__bottom">
          <Button
            isLoading={clientPatchRequest && clientPatchRequest.isPending}
            disabled={!(
              isValidEmail &&
              isValidToken &&
              isValidSubdomain &&
              client.isValid &&
              client.isDirty
            )}
            title={`${this.isZendeskConfigured ? "Save" : "Add"} integration`}
            text={`${this.isZendeskConfigured ? "Save" : "Add"} integration`}
            icon="Zendesk"
            onClick={() => this.handleSubmit()}
          />
        </div>
      </div>
    );
  }
}

ModalZendeskTicketingConfig.propTypes = {
  client: PropTypes.instanceOf(Client).isRequired,
  clientPatchRequest: PropTypes.instanceOf(RequestState),
  updateClient: PropTypes.func.isRequired,
  saveClient: PropTypes.func.isRequired
};

ModalZendeskTicketingConfig.defaultProps = {
  clientPatchRequest: null
};

/**
 * @param {Object} state
 * @returns {Object}
 */
function mapState(state) {
  return {
    client: getResource(state, "client"),
    clientPatchRequest: getResourcesRequest(state, "client", "patchOne")
  };
}

/**
 * @param {Function} dispatch
 * @returns {Object.<Function>}
 */
function mapDispatch(dispatch) {
  return {
    updateClient: (id, data) => dispatch(updateOne("client", id, data)),
    saveClient: id => dispatch(saveClientAction(id))
  };
}

const Connector = connect(mapState, mapDispatch)(ModalZendeskTicketingConfig);

export default Connector;
